import { Tab, TAB_TYPE, useToast } from '@repo/design-system/index'
import { QIcon_cart, QIcon_menu_button_hamburger } from '@repo/icons/index'
import { ActivityComponentType } from '@stackflow/react'
import dynamic from 'next/dynamic'
import { useEffect, useState } from 'react'
import { useEventTracking } from '../../../helpers/gtmHelper'
import apis from '../../../modules/apis'
import { ILessonReviewData } from '../../../modules/apis/student/lesson'
import { useCustomerType } from '../../../modules/apis/student/products'
import { axiosGet } from '../../../modules/axiosHelper'
import { useCurrentUser } from '../../../modules/context/UserContext'
import { APP_SCREEN_NAME } from '../../../modules/hooks/common/appRouterVars'
import useAppRouter, { sendPostMessageOnlyMessage } from '../../../modules/hooks/common/useAppRouter'
import useReturnFullUrl from '../../../modules/hooks/webview/common/useReturnFullUrl'
import { n_lesson, n_upcoming_lessons, s_menu } from '../../../modules/i18n/strings/auto/wording'
import useTranslation from '../../../modules/i18n/useTranslation'
import urls from '../../../modules/urls'
import { TUTOR_TYPE } from '../../../modules/vars'
import SelectMaterialBody from '../../new-portal/lessons/materials/SelectMaterialBody'
import TutorListBody from '../../student/new-portal/tutors/tutorListBody'
import WebviewStackContainer from '../common/WebviewStackContainer'

const ReviewHomeBody = dynamic(() => import('../../new-portal/review/ReviewHomeBody'))
const UpcomingActivity = dynamic(() => import('./UpcomingActivity'))

interface Props {
  tabIndex: number
  setTabIndex: (index: number) => void
  data: ILessonReviewData
  upcomingCnt?: number
}

const LessonHomeTopBar = ({ activeTabIdx, setActiveTabIdx, upcomingCnt }) => {
  const { t } = useTranslation()
  const router = useAppRouter()
  const { data: customerType } = useCustomerType()
  const getFullUrl = useReturnFullUrl()

  const handleClickMenu = () => {
    sendPostMessageOnlyMessage(JSON.stringify({ type: 'CLICK_MENU' }))
  }

  const onClickPurchaseList = () => {
    router.push(`${urls.student.newPortal.pricing.index}`, {
      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
      appSendData: {
        url: getFullUrl(`${urls.student.portal.pricing.index}?app=true`),
      },
    })
  }

  const isDirectPurchaser = customerType?.isDirectPurchaser

  return (
    <div className="flex w-full flex-col">
      <div className="flex flex-row items-center justify-between px-[20px] h-[44px]">
        <div className="flex h-full flex-row items-center justify-center gap-x-[20px] Title1-20Bd">
          {t(s_menu.portalv2.conversation)}
        </div>
        <div className="flex h-full flex-row items-center justify-center gap-x-[20px]">
          {!isDirectPurchaser && <QIcon_cart onClick={onClickPurchaseList} width={24} height={24} />}
          <QIcon_menu_button_hamburger onClick={handleClickMenu} width={24} height={24} />
        </div>
      </div>
      <div className="h-[52px] w-full border-b-1 border-gray-100">
        <Tab
          className="h-[46px]"
          type={TAB_TYPE.SCROLL}
          borderNone={true}
          activeTabIdx={activeTabIdx}
          setActiveTabIdx={setActiveTabIdx}
        >
          <Tab.TabItem name={t(n_lesson.tab_upcoming(upcomingCnt))} />
          <Tab.TabItem name={t(s_menu.conversation.new_review_lessons)} />
          <Tab.TabItem name={t(n_lesson.common.tutor)} />
          <Tab.TabItem name={t(n_lesson.common.material)} />
        </Tab>
      </div>
    </div>
  )
}

const LessonHomeActivity: ActivityComponentType = ({ params }: { params: { tab?: number } }) => {
  const { tab } = params

  const router = useAppRouter()
  const { toastSuccess } = useToast()
  const { t } = useTranslation()
  const { sendPageViewEvent } = useEventTracking()
  const getFullUrl = useReturnFullUrl()
  const { currentUser } = useCurrentUser()

  const [tabIndex, setTabIndex] = useState(+tab)
  console.log(tabIndex, 'tabIndex')
  const [upcomingCnt, setUpcomingCnt] = useState(0)

  const getInitialData = async () => {
    const { update } = router.query
    if (update) {
      if (update === 'material') toastSuccess(t(n_upcoming_lessons.edit_materials.toast_select))
      const { pathname, query } = router
      delete query.update
      router.replace({ pathname, query }, undefined, { shallow: true })
    }
  }

  const getUpcomingCnt = async () => {
    const response = await axiosGet(apis.lessons.upcomingsV5())
    if (response) {
      setUpcomingCnt(response.upcoming_lessons ? response.upcoming_lessons.length : 0)
    }
  }

  const handleClickAIIcon = () => {
    router.push(urls.webview.conversationalAI.list, {
      appScreenName: APP_SCREEN_NAME.STACK_SCREEN,
      appSendData: {
        url: getFullUrl(urls.webview.conversationalAI.list),
      },
    })
  }

  useEffect(() => {
    getInitialData()
    getUpcomingCnt()
  }, [])

  useEffect(() => {
    if (!router) return
    if (tabIndex === 0) {
      sendPageViewEvent({ page_name: 'upcoming_lessons' })
    } else {
      sendPageViewEvent({ page_name: 'past_lessons' })
    }
  }, [tabIndex, router])

  return (
    <WebviewStackContainer
      height={'96px'}
      border={false}
      showBookButton={tabIndex == 0 || tabIndex == 1}
      title={<LessonHomeTopBar activeTabIdx={tabIndex} setActiveTabIdx={setTabIndex} upcomingCnt={upcomingCnt} />}
    >
      {
        <>
          {[
            () => <UpcomingActivity />,
            () => <ReviewHomeBody isWebView={true} />,
            () => <TutorListBody type={TUTOR_TYPE.RECOMMEND_TUTOR} />,
            () => (
              <div className="mx-[16px]">
                <SelectMaterialBody isList={true} />
              </div>
            ),
          ][tabIndex || 0]()}
        </>
      }
    </WebviewStackContainer>
  )
}

export default LessonHomeActivity
